import { computed, type Ref } from "vue";
import { useGetDiagnosticImages } from "@/stores/queries/diagnostic-images.query";
import { useGetReportImages } from "@/stores/queries/report-images.query";
import type {
  MergedReportImage,
  ReportImage,
  ReportImageStatus,
} from "@/types/report-images.types";
import type { DiagnosticImage } from "@/types/diagnostic-images.types";

/**
 * Composable that merges DiagnosticImages with their corresponding ReportImages.
 *
 * This provides a unified view of all photos in a diagnostic with their
 * report-specific metadata (numbering, description, etc.) if available.
 *
 * If a DiagnosticImage doesn't have a corresponding ReportImage yet,
 * it's treated as a "virtual" entry with status UNPROCESSED.
 */
export function useMergedReportImages(
  diagnosticId: Ref<number | undefined>,
  reportId: Ref<number | undefined>
) {
  // Fetch all DiagnosticImages for this diagnostic
  const {
    data: diagnosticImages,
    isLoading: isLoadingDiagnosticImages,
    refetch: refetchDiagnosticImages,
  } = useGetDiagnosticImages(diagnosticId);

  // Fetch all ReportImages for this report
  const {
    data: reportImages,
    isLoading: isLoadingReportImages,
    refetch: refetchReportImages,
  } = useGetReportImages(reportId);

  // Merge diagnostic images with report images
  const mergedImages = computed<MergedReportImage[]>(() => {
    if (!diagnosticImages.value || !reportId.value) return [];

    // Create a map of reportImages by diagnosticImageId for fast lookup
    const reportImageMap = new Map<number, ReportImage>();
    if (reportImages.value) {
      for (const ri of reportImages.value) {
        reportImageMap.set(ri.diagnosticImageId, ri);
      }
    }

    // Merge each diagnostic image with its report image (if exists)
    return diagnosticImages.value.map((di: DiagnosticImage) => {
      const ri = reportImageMap.get(di.id);

      if (ri) {
        // ReportImage exists - use its data
        const status = getDerivedStatus(ri);
        return {
          diagnosticImageId: di.id,
          diagnosticId: di.diagnosticId,
          fileId: di.fileId,
          file: di.file,
          reportImageId: ri.id,
          reportId: ri.reportId,
          numbering: ri.numbering,
          photoNumber: di.photoNumber,
          description: ri.description,
          location: ri.location,
          sortedSectionId: ri.sortedSectionId,
          excluded: ri.excluded,
          isOverview: ri.isOverview,
          overviewSectionId: ri.overviewSectionId,
          findings: ri.findings,
          sortedSection: ri.sortedSection,
          overviewSection: ri.overviewSection,
          status,
          isVirtual: false,
        };
      } else {
        // No ReportImage yet - create a virtual entry
        return {
          diagnosticImageId: di.id,
          diagnosticId: di.diagnosticId,
          fileId: di.fileId,
          file: di.file,
          reportImageId: null,
          reportId: reportId.value!,
          numbering: null,
          photoNumber: di.photoNumber,
          description: null,
          location: null,
          sortedSectionId: null,
          excluded: false,
          isOverview: false,
          overviewSectionId: null,
          findings: [],
          sortedSection: null,
          overviewSection: null,
          status: "UNPROCESSED" as ReportImageStatus,
          isVirtual: true,
        };
      }
    });
  });

  // Loading state
  const isLoading = computed(
    () => isLoadingDiagnosticImages.value || isLoadingReportImages.value
  );

  // Refetch both queries
  const refetch = () => {
    refetchDiagnosticImages();
    refetchReportImages();
  };

  // Filter helpers
  const unprocessedImages = computed(() =>
    mergedImages.value.filter((img) => img.status === "UNPROCESSED")
  );

  const excludedImages = computed(() =>
    mergedImages.value.filter((img) => img.status === "EXCLUDED")
  );

  const sortedImages = computed(() =>
    mergedImages.value.filter((img) => img.status === "SORTED")
  );

  const placedImages = computed(() =>
    mergedImages.value.filter((img) => img.status === "PLACED")
  );

  const overviewImages = computed(() =>
    mergedImages.value.filter((img) => img.isOverview)
  );

  const imagesBySectionId = computed(() => {
    const map = new Map<number | null, MergedReportImage[]>();
    for (const img of mergedImages.value) {
      const sectionId = img.sortedSectionId ?? null;
      if (!map.has(sectionId)) {
        map.set(sectionId, []);
      }
      map.get(sectionId)!.push(img);
    }
    return map;
  });

  // Find a merged image by diagnostic image ID
  const findByDiagnosticImageId = (
    diagnosticImageId: number
  ): MergedReportImage | undefined => {
    return mergedImages.value.find(
      (img) => img.diagnosticImageId === diagnosticImageId
    );
  };

  // Find a merged image by report image ID
  const findByReportImageId = (
    reportImageId: number
  ): MergedReportImage | undefined => {
    return mergedImages.value.find(
      (img) => img.reportImageId === reportImageId
    );
  };

  return {
    mergedImages,
    isLoading,
    refetch,
    // Filter helpers
    unprocessedImages,
    excludedImages,
    sortedImages,
    placedImages,
    overviewImages,
    imagesBySectionId,
    // Lookup helpers
    findByDiagnosticImageId,
    findByReportImageId,
    // Raw queries for advanced use
    diagnosticImages,
    reportImages,
  };
}

function getDerivedStatus(
  image: Pick<MergedReportImage, "excluded" | "findings" | "sortedSectionId">
): ReportImageStatus {
  if (image.excluded) return "EXCLUDED";
  if (image.findings && image.findings.length > 0) return "PLACED";
  if (image.sortedSectionId) return "SORTED";
  return "UNPROCESSED";
}
